// Visit-detail modal — the dashboard/visits "View visit" pop-out. Ported from Luan's showVisitDetail
// (vd-* layout). Three views in one dialog: detail → reschedule / cancel, all reusing the REAL
// appointment commands (useAppointmentActions) and the same lead-window guards as the page. Clinical
// vitals (Luan's vd-vitals on past visits) are intentionally omitted — that data lives in Healthie.
import { useState } from "react";
import { Link } from "react-router-dom";
import { useApi } from "../api";
import { useAsync } from "../lib/useAsync";
import { useAppointmentActions } from "../hooks/useAppointmentActions";
import { useDoctorsApi } from "../lib/doctorsApi";
import { withinJoinWindow } from "../lib/window-guard";
import { formatDateTime, formatDay, labelText } from "../lib/format";
import { Modal } from "@championcare/ui";
import { SlotPicker } from "./SlotPicker";
import { DoctorCard } from "./DoctorCard";
import { ErrorState, Loading } from "./ui";
import type { AppointmentView, DoctorSummary } from "@forcebase-ai/schema/patient";

function statusPillClass(status: string): string {
  if (status === "completed") return "completed";
  if (status === "cancelled") return "canceled";
  return "upcoming";
}

const Ic = {
  clock: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>,
  video: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><polygon points="23 7 16 12 23 17 23 7" /><rect x="1" y="5" width="15" height="14" rx="2" /></svg>,
  info: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>,
  play: <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 14, height: 14 }}><polygon points="5 3 19 12 5 21 5 3" /></svg>,
};

type View = "detail" | "reschedule" | "cancel" | "doctor";

export function VisitModal({
  appt,
  doctor,
  onClose,
  onChanged,
  initialView = "detail",
}: {
  appt: AppointmentView;
  doctor: DoctorSummary | undefined;
  onClose: () => void;
  onChanged: () => void;
  initialView?: View;
}) {
  const api = useApi();
  const actions = useAppointmentActions();
  const doctorsApi = useDoctorsApi();
  const [view, setView] = useState<View>(initialView);
  const [slot, setSlot] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [msg, setMsg] = useState<string | null>(null);

  const doctorName = doctor ? `Dr. ${doctor.first_name} ${doctor.last_name}` : "your doctor";
  // Lazily load the full doctor profile only when the in-modal doctor card is opened.
  const doctorProfile = useAsync(
    () => (view === "doctor" ? doctorsApi.get(appt.doctor_id) : Promise.resolve(null)),
    [view, appt.doctor_id],
  );
  // "with Dr. X" — when we know the doctor, the name opens the profile card in-place (Back returns
  // here). Falls back to plain text when the doctor is unknown.
  const withDoctor = doctor ? (
    <>
      with{" "}
      <button type="button" className="vd-doctor-link" onClick={() => setView("doctor")}>
        {doctorName}
      </button>
    </>
  ) : (
    <>with {doctorName}</>
  );
  const open = appt.status !== "cancelled" && appt.status !== "completed";
  const canJoin = withinJoinWindow(appt) || appt.status === "live";

  // Class-B visit recap (non-clinical thin summary we're allowed to surface). Only fetched for past
  // visits; the full clinical record (notes/vitals/findings/Rx) is NEVER rendered here — it lives in
  // Healthie and stays with the care team (patients do not open the chart from here, per FB; D5).
  const summaries = useAsync(() => (open ? Promise.resolve([]) : api.visitSummaries()), [appt.id]);
  const recap = (summaries.data ?? []).find(
    (s) => s.healthie_appointment_id && s.healthie_appointment_id === appt.healthie_appointment_id,
  );

  async function confirmReschedule() {
    if (!slot) return;
    setMsg(null);
    const res = await actions.reschedule(appt.id, { new_scheduled_at: slot, reason: reason.trim() || "Member rescheduled" });
    if (res.ok) {
      onChanged();
      onClose();
    } else if (res.code === "slot_unavailable") {
      setMsg("That time was just taken — pick another.");
      setSlot(null);
    } else if (res.code === "too_late") {
      setMsg("It's now too late to self-reschedule — please contact support.");
    } else {
      setMsg(res.message ?? "Could not reschedule.");
    }
  }

  async function confirmCancel() {
    if (!reason.trim()) {
      setMsg("Please tell us why you're cancelling.");
      return;
    }
    const res = await actions.cancel(appt.id, { reason: reason.trim() });
    if (res.ok) {
      onChanged();
      onClose();
    } else if (res.code === "too_late") {
      setMsg("It's now too late to self-cancel — please contact support.");
    } else {
      setMsg(res.message ?? "Could not cancel.");
    }
  }

  return (
    <Modal open onClose={onClose} labelledBy="vd-title" wide>
      {view === "detail" && open && (
        <>
          <div className="dialog-eyebrow">Upcoming visit</div>
          <div className="dialog-title" id="vd-title">{formatDateTime(appt.scheduled_at)}</div>
          <div className="dialog-body" style={{ marginBottom: 18 }}>{withDoctor}</div>

          <div className="vd-meta">
            <div className="vd-meta-row">{Ic.clock}<span>{appt.duration_minutes}-min telehealth visit</span></div>
            <div className="vd-meta-row">{Ic.video}<span>Video call</span></div>
            <div className="vd-meta-row">{Ic.info}<span>{labelText(appt.label)}</span></div>
          </div>

          <div className="vd-what-to-expect">
            <h4>What to expect</h4>
            <ul>
              <li>Join from any device — no download required on desktop.</li>
              <li>Your doctor admits you from the waiting room at your start time.</li>
              <li>Prescriptions and a visit recap arrive after the call.</li>
            </ul>
          </div>

          {canJoin && (
            <Link className="btn btn-primary vd-join-btn" to={`/visits/${appt.id}/room`} onClick={onClose}>{Ic.play}Join visit now</Link>
          )}
          <div className="vd-actions">
            <button className="btn btn-ghost" onClick={() => { setMsg(null); setView("reschedule"); }}>Reschedule</button>
            <button className="btn btn-danger" onClick={() => { setMsg(null); setReason(""); setView("cancel"); }}>Cancel visit</button>
          </div>
        </>
      )}

      {view === "detail" && !open && (
        <>
          <div className="dialog-eyebrow">{appt.status === "cancelled" ? "Canceled visit" : "Visit summary"}</div>
          <div className="dialog-title" id="vd-title">{formatDateTime(appt.scheduled_at)}</div>
          <div className="dialog-body" style={{ marginBottom: 18 }}>{withDoctor}</div>

          <div className="vd-meta">
            <div className="vd-meta-row">{Ic.clock}<span>{appt.duration_minutes}-min telehealth visit</span></div>
            <div className="vd-meta-row">{Ic.video}<span>Video call</span></div>
            <div className="vd-meta-row">{Ic.info}<span>{labelText(appt.label)}</span></div>
            <div className="vd-meta-row"><span className={`visit-status ${statusPillClass(appt.status)}`}>{appt.status}</span></div>
          </div>

          {recap && (
            <div className="vd-section">
              <div className="vd-section-label">Summary</div>
              <div className="vd-section-content">{recap.summary_text}</div>
            </div>
          )}

          <div className="vd-section">
            <div className="vd-section-label">Full record</div>
            <div className="vd-section-content">
              Your visit notes, vitals, findings, and prescriptions are kept securely in your protected chart.
            </div>
          </div>
        </>
      )}

      {view === "doctor" && (
        <>
          {doctorProfile.loading || !doctorProfile.data ? (
            <Loading />
          ) : (
            <DoctorCard doctor={doctorProfile.data} />
          )}
          <div className="dialog-actions" style={{ marginTop: 22 }}>
            <button className="btn btn-ghost" onClick={() => setView("detail")}>Back</button>
          </div>
        </>
      )}

      {view === "reschedule" && (
        <>
          <div className="dialog-eyebrow">Reschedule</div>
          <div className="dialog-title">Pick a <em>new time.</em></div>
          <div className="resched-current">
            Currently scheduled for <strong>{formatDay(appt.scheduled_at)}</strong> with <em>{doctorName}</em>. Pick a new day and time below.
          </div>
          <SlotPicker doctorId={appt.doctor_id} value={slot} onChange={setSlot} />
          <div className="field" style={{ marginTop: 14 }}>
            <label className="field-label">Reason (optional)</label>
            <input className="field-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. schedule conflict" />
          </div>
          {msg && <div style={{ marginTop: 12 }}><ErrorState message={msg} /></div>}
          <div className="dialog-actions" style={{ marginTop: 22 }}>
            <button className="btn btn-ghost" onClick={() => setView("detail")} disabled={actions.busy}>Back</button>
            <button className="btn btn-primary" disabled={!slot || actions.busy} onClick={confirmReschedule}>
              {actions.busy ? "Rescheduling…" : "Confirm new time"}
            </button>
          </div>
        </>
      )}

      {view === "cancel" && (
        <>
          <div className="dialog-eyebrow">Cancel visit</div>
          <div className="dialog-title">Cancel this <em>visit?</em></div>
          <div className="dialog-body">
            Visits are included in your membership — there&rsquo;s no per-visit charge or refund.
            Cancelling late may count toward your fair-use limits.
          </div>
          <div className="field">
            <label className="field-label">Reason</label>
            <input className="field-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why are you cancelling?" />
          </div>
          {msg && <div style={{ marginTop: 12 }}><ErrorState message={msg} /></div>}
          <div className="dialog-actions" style={{ marginTop: 22 }}>
            <button className="btn btn-ghost" onClick={() => setView("detail")} disabled={actions.busy}>Keep visit</button>
            <button className="btn btn-danger" disabled={actions.busy} onClick={confirmCancel}>
              {actions.busy ? "Cancelling…" : "Confirm cancellation"}
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}
